import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, Navigate } from 'react-router-dom'
import classnames from 'classnames'

import PageTitle from 'components/atoms/PageTitle'
import PageLoading from 'components/atoms/PageLoading'
import SessionsPlanning from 'components/organisms/SessionsPlanning'
import SessionsConfirmation from 'components/organisms/SessionsConfirmation'
import {
  checkAssignment,
  getLastSessionState,
  getSessionsStepUrl,
  SESSIONS_STEPS_LIST,
  SESSIONS_STEP_DISABLED,
} from 'components/organisms/Sessions/sessionsLib'
import Stepper from 'components/atoms/Stepper'
import NotFound from 'components/atoms/NotFound'
import useSessionSteps from 'components/organisms/Sessions/useSessionSteps'
import useSessionStepNavigation from 'components/organisms/Sessions/useSessionStepNavigation'
import PageSessionSelection from 'components/atoms/PageSessionSelection'
import SessionsCarsPricing from 'components/organisms/SessionsCarsPricing'

import useSession from 'hooks/useSession'
import useSessionsList from 'hooks/useSessionsList'

import { ROLES_SIXT, ROLE_B2C, ROLE_READ_ONLY, useAuth } from '../../auth'

// eslint-disable-next-line sonarjs/cognitive-complexity
export default function SessionsStepsRouter() {
  const { t } = useTranslation()

  const { id, step } = useParams()
  const [showHeader, setShowHeader] = useState(true)

  const {
    isFetching: isFetchingSessions,
    fetchSessions,
    sessions,
  } = useSessionsList()

  const {
    isFetching: isFetchingSession,
    isUpdating: isUpdatingSession,
    fetchSession,
    updateDate,
    updateAllDates,
    updatePrices,
    fetchCarsData,
    confirmStep,
    session,
    error,
  } = useSession()

  const { fetchCurrentSession, session: currentActiveSession } = useSession()

  const {
    pricingStepCars,
    initSteps,
    handleStepValidation,
    getStepsWithState,
  } = useSessionSteps()

  useEffect(() => {
    fetchSessions()
  }, [fetchSessions])

  useEffect(() => {
    if (!sessions.length) {
      return
    }
    fetchSession(id)
  }, [fetchSession, updateDate, sessions, id])

  useEffect(() => {
    if (!sessions.length) {
      return
    }
    fetchCurrentSession()
  }, [fetchCurrentSession, sessions])

  useEffect(() => {
    if (!session || !Object.keys(session).length) {
      return
    }
    initSteps(session)
  }, [initSteps, session])

  const assignment = useMemo(() => checkAssignment(session), [session])

  const steps = useMemo(() => getStepsWithState(), [getStepsWithState])

  const { goToStep } = useSessionStepNavigation()

  const handleStep = (index) => {
    goToStep(session.sessionId, index)
  }

  const handleConfirm = async () => {
    await confirmStep(session.sessionId, step)
  }

  const { hasOneOfRoles } = useAuth()
  const canAccess = useMemo(() => hasOneOfRoles(ROLES_SIXT), [hasOneOfRoles])
  const userRoleHasReadOnly = useMemo(
    () => hasOneOfRoles([ROLE_READ_ONLY, ROLE_B2C]),
    [hasOneOfRoles]
  )

  if (!canAccess) {
    return <Navigate to="/agenda" />
  }

  return (
    <div className={classnames('content-container', { details: !showHeader })}>
      {error && Number(error.response.status) === 404 ? (
        <NotFound />
      ) : isFetchingSessions ||
        isFetchingSession ||
        !Object.keys(session).length ||
        !Object.keys(steps).length ? (
        <PageLoading />
      ) : !step ||
        !SESSIONS_STEPS_LIST.includes(step) ||
        steps.find((s) => s.step === step).state === SESSIONS_STEP_DISABLED ? (
        <Navigate
          to={`/sessions/${session.sessionId}/${getLastSessionState(steps)}`}
        />
      ) : (
        <>
          {(showHeader || step !== SESSIONS_STEPS_LIST[5]) && (
            <PageTitle
              label={t(`sessions.${step}.title`)}
              rightColumn={
                <PageSessionSelection
                  session={session}
                  currentActiveSession={currentActiveSession}
                  task="sessionCreation"
                  handleStepUrl={getSessionsStepUrl}
                  sessionsList={sessions}
                  readOnly={isUpdatingSession}
                />
              }
            />
          )}
          {step === SESSIONS_STEPS_LIST[0] ? (
            <>
              <Stepper steps={steps} activeStep={0} handleStep={handleStep} />
              <SessionsPlanning
                data-testid="session-planning"
                id={id}
                session={session}
                updateDate={updateDate}
                handleValidation={(sessionState) =>
                  handleStepValidation(step, sessionState)
                }
                handleConfirm={handleConfirm}
                handleBeforeAssignmentConfirm={updateAllDates}
                assignment={assignment}
                readOnly={isUpdatingSession}
                contentIsReadOnly={userRoleHasReadOnly}
              />
            </>
          ) : step === SESSIONS_STEPS_LIST[1] ? (
            <>
              <Stepper steps={steps} activeStep={2} handleStep={handleStep} />
              <SessionsCarsPricing
                id={id}
                data-testid="session-pricing"
                session={session}
                stepCars={pricingStepCars}
                update={updatePrices}
                handleValidation={() => handleStepValidation(step)}
                handleConfirm={handleConfirm}
                fetchData={fetchCarsData}
                confirmed={!!session[`${step}AcceptedAt`]}
                readOnly={isUpdatingSession || !!session.confirmationAcceptedAt}
                contentIsReadOnly={userRoleHasReadOnly}
              />
            </>
          ) : (
            <>
              {showHeader && (
                <Stepper steps={steps} activeStep={5} handleStep={handleStep} />
              )}
              <SessionsConfirmation
                id={id}
                data-testid="session-confirmation"
                session={session}
                updateDate={updateDate}
                handleValidation={() => handleStepValidation(step)}
                handleConfirm={handleConfirm}
                confirmed={!!session[`${step}AcceptedAt`]}
                readOnly={isUpdatingSession || !!session.confirmationAcceptedAt}
                contentIsReadOnly={userRoleHasReadOnly}
                setShowHeader={setShowHeader}
              />
            </>
          )}
        </>
      )}
    </div>
  )
}
